import { useState, useEffect } from 'react';
import { CheckCheck, Info, Plus, Edit, Trash2, Save, X, Loader2 } from 'lucide-react';
import { approvalAPI } from '../services/api';
import { toast } from 'sonner';

export default function ApprovalWorkflowsPage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', ticket_type: 'sr', condition_field: 'urgency_level', condition_value: '', required_levels: 2 });

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await approvalAPI.listConfigs();
      setConfigs(res.data?.results || res.data || []);
    } catch { setConfigs([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); // eslint-disable-next-line
  }, []);

  const handleSave = async () => {
    if (!form.name || !form.condition_value) { toast.error('Fill all required fields'); return; }
    try {
      if (editing) {
        await approvalAPI.updateConfig(editing, form);
        toast.success('Workflow updated');
      } else {
        await approvalAPI.createConfig(form);
        toast.success('Workflow created');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', ticket_type: 'sr', condition_field: 'urgency_level', condition_value: '', required_levels: 2 });
      fetch();
    } catch (err) {
      toast.error(JSON.stringify(err.response?.data) || 'Failed');
    }
  };

  const startEdit = (c) => {
    setForm({ name: c.name, ticket_type: c.ticket_type, condition_field: c.condition_field, condition_value: c.condition_value, required_levels: c.required_levels });
    setEditing(c.id);
    setShowForm(true);
  };

  return (
    <div data-testid="approval-workflows-page">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
          <CheckCheck className="w-6 h-6 text-emerald-600" /> Approval Workflows
        </h2>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', ticket_type: 'sr', condition_field: 'urgency_level', condition_value: '', required_levels: 2 }); }} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600" data-testid="add-workflow-btn">
          <Plus className="w-4 h-4" /> Add Workflow
        </button>
      </div>

      <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 mb-6 flex items-start gap-3">
        <Info className="w-5 h-5 text-emerald-700 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-emerald-900">Approval levels are determined by ticket type and conditions. Configure routing rules to match your organization's policies.</p>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6 mb-6" data-testid="workflow-form">
          <h3 className="font-semibold mb-4">{editing ? 'Edit' : 'New'} Approval Workflow</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <input placeholder="Workflow Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
            <select value={form.ticket_type} onChange={e => setForm({...form, ticket_type: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white">
              <option value="sr">Service Request</option>
              <option value="cr">Change Request</option>
            </select>
            <select value={form.condition_field} onChange={e => setForm({...form, condition_field: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white">
              <option value="urgency_level">Urgency Level</option>
              <option value="risk_level">Risk Level</option>
              <option value="change_type">Change Type</option>
            </select>
            <input placeholder="Condition Value *" value={form.condition_value} onChange={e => setForm({...form, condition_value: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
            <select value={form.required_levels} onChange={e => setForm({...form, required_levels: parseInt(e.target.value)})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white">
              <option value={1}>1 Level</option>
              <option value={2}>2 Levels</option>
              <option value={3}>3 Levels</option>
            </select>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-1.5 hover:bg-sky-600"><Save className="w-4 h-4" /> Save</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="bg-slate-100 text-slate-600 px-4 py-2 rounded-xl text-sm flex items-center gap-1.5"><X className="w-4 h-4" /> Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 overflow-hidden shadow-sm">
          <table className="w-full" data-testid="workflows-table">
            <thead className="bg-slate-50 dark:bg-slate-900 text-xs text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Workflow Name</th>
                <th className="px-4 py-3 text-center font-semibold">Ticket Type</th>
                <th className="px-4 py-3 text-left font-semibold">Condition</th>
                <th className="px-4 py-3 text-center font-semibold">Required Levels</th>
                <th className="px-4 py-3 text-center font-semibold">Status</th>
                <th className="px-4 py-3 text-center font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-slate-700 text-sm">
              {configs.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                  <td className="px-4 py-3 font-medium dark:text-white">{c.name}</td>
                  <td className="px-4 py-3 text-center"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${c.ticket_type === 'sr' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>{c.ticket_type === 'sr' ? 'Service Request' : 'Change Request'}</span></td>
                  <td className="px-4 py-3 text-slate-600">{c.condition_field} = {c.condition_value}</td>
                  <td className="px-4 py-3 text-center"><span className="bg-sky-100 text-sky-800 px-2 py-1 rounded-full text-xs font-semibold">{c.required_levels} Level{c.required_levels > 1 ? 's' : ''}</span></td>
                  <td className="px-4 py-3 text-center"><span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full text-xs">{c.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-1">
                      <button onClick={() => startEdit(c)} className="text-slate-400 hover:text-sky-600 p-1"><Edit className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {configs.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No approval workflows configured</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
